"use client"

import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import {
  calculateArtificialTurfCost,
  type ArtificialTurfCostResult,
} from "@/lib/artificial-turf-cost-calculator"
import type { TurfGrade, TurfInstallMethod } from "@/data/artificial-turf-costs"
import { setBottomDockHeight } from "@/hooks/use-bottom-dock-inset"
import { useSavedInputs } from "@/hooks/use-saved-inputs"
import { getRegionFromZipCode } from "@/data/regional-climate"
import { ZipCodeInput } from "@/components/ui/zip-code-input"

// ── Tokens - neutral palette matching the lawn cost estimator; mono only for
//     live-output numerics so they read as data. ─────────────────────────────
const TURF_BG = "#ffffff"
const TURF_INK = "#111827" // gray-900
const TURF_RULE = "#e5e7eb" // gray-200
const TURF_ACCENT = "#15803d" // green-700, matches site CTA
const TURF_PAPER_DEEP = "#f3f4f6" // gray-100
const TURF_MUTED = "rgba(17,24,39,0.62)"
const TURF_MUTED_2 = "rgba(17,24,39,0.45)"
const FONT_MONO = `var(--font-jetbrains-mono), ui-monospace, 'SF Mono', Menlo, monospace`

interface ArtificialTurfCostFormProps {
  initialValues?: {
    zipCode?: string
    area?: number
  }
  /** Called whenever the live calculated result changes. Null until the ZIP
   *  resolves to a known region (the only required input with no default). */
  onResultChange?: (
    result: ArtificialTurfCostResult | null,
    inputs?: { removeExistingLawn: boolean },
  ) => void
  error?: string | null
}

// ── Inline icons ───────────────────────────────────────────────────────
function Ico({ size = 16, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  )
}
const IcoArea = (p: { size?: number }) => <Ico {...p}><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 3v18" /></Ico>
const IcoPin = (p: { size?: number }) => <Ico {...p}><path d="M12 21s-7-7.5-7-12a7 7 0 0114 0c0 4.5-7 12-7 12z" /><circle cx="12" cy="9" r="2.5" /></Ico>
const IcoGrade = (p: { size?: number }) => <Ico {...p}><path d="M4 20h16" /><path d="M7 20c0-4 1-7 3-9" /><path d="M12 20c0-5 1-8 3-10" /><path d="M17 20c0-3 0-6 2-8" /></Ico>
const IcoWrench = (p: { size?: number }) => <Ico {...p}><path d="M14.7 6.3a4 4 0 105.6 5.6L21 11l-3-3 1.3-1.3a4 4 0 00-4.6 0z" /><path d="M14 8L4 18l2 2 10-10" /></Ico>
const IcoCheck = (p: { size?: number }) => <Ico {...p}><path d="M5 12l4 4 10-10" /></Ico>
const IcoChev = (p: { size?: number }) => <Ico {...p}><path d="M6 9l6 6 6-6" /></Ico>
const IcoLock = (p: { size?: number }) => <Ico {...p}><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></Ico>
const IcoEdit = (p: { size?: number }) => <Ico {...p}><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M14 6l4 4" /></Ico>

// ── Static data ────────────────────────────────────────────────────────
const AREA_PRESETS: Array<{ value: number; label: string; note: string }> = [
  { value: 300, label: "Small", note: "front strip / patio" },
  { value: 800, label: "Typical", note: "small backyard" },
  { value: 1500, label: "Large", note: "full backyard" },
  { value: 3000, label: "XL", note: "big yard / commercial" },
]

const GRADES: Array<{ id: TurfGrade; name: string; pitch: string; incl: string[]; recommended?: boolean }> = [
  { id: "economy", name: "Economy", pitch: "Budget rolls for light-use or pet areas.", incl: ["Lower face weight", "Shorter warranty", "Good for low traffic"] },
  { id: "standard", name: "Standard", pitch: "The mid-grade most homeowners choose.", incl: ["Realistic look", "8-15 yr warranty", "Handles normal traffic"], recommended: true },
  { id: "premium", name: "Premium", pitch: "Heaviest, most realistic, longest-lasting.", incl: ["High face weight", "15+ yr warranty", "Best for heavy use"] },
]

const METHODS: Array<{ id: TurfInstallMethod; name: string; meta: string; blurb: string }> = [
  { id: "diy", name: "Do it yourself", meta: "Time: a weekend+", blurb: "You buy materials and do the demo, base, and install. No labor line." },
  { id: "professional", name: "Hire a pro", meta: "Time: ~0 hr", blurb: "A crew handles grading, base, seaming, and infill." },
]

// ── Reusable bits ──────────────────────────────────────────────────────
function StepCircle({ step, accent }: { step: number; accent?: boolean }) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: 27,
        height: 27,
        flexShrink: 0,
        borderRadius: "50%",
        background: TURF_PAPER_DEEP,
        border: accent ? `1.5px solid ${TURF_ACCENT}` : `1px solid ${TURF_RULE}`,
        boxShadow: accent ? `0 0 0 3px ${TURF_ACCENT}22` : "none",
        color: accent ? TURF_ACCENT : TURF_INK,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: FONT_MONO,
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      {step}
    </span>
  )
}

function Label({
  children,
  hint,
  step,
  accent,
}: {
  children: React.ReactNode
  hint?: React.ReactNode
  step?: number
  accent?: boolean
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        marginBottom: 10,
        columnGap: 12,
        rowGap: 4,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: "0 1 auto" }}>
        {step != null && <StepCircle step={step} accent={accent} />}
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: TURF_INK, opacity: 0.55 }}>
          {children}
        </div>
      </div>
      {hint && (
        <div
          style={{
            flex: "1 1 150px",
            minWidth: 0,
            fontSize: 12,
            color: TURF_MUTED,
            textAlign: "right",
          }}
        >
          {hint}
        </div>
      )}
    </div>
  )
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        background: "#fff",
        border: `1px solid ${TURF_RULE}`,
        borderRadius: 12,
        padding: 18,
      }}
    >
      {children}
    </div>
  )
}

// ── Area field ─────────────────────────────────────────────────────────
function AreaField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(String(value))
  useEffect(() => { if (!editing) setDraft(String(value)) }, [value, editing])

  const min = 100, max = 10000
  const toSlider = (v: number) =>
    Math.round(((Math.log(v) - Math.log(min)) / (Math.log(max) - Math.log(min))) * 1000)
  const fromSlider = (s: number) =>
    Math.round(Math.exp(Math.log(min) + (s / 1000) * (Math.log(max) - Math.log(min))) / 25) * 25

  return (
    <div>
      <Label step={1}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoArea size={13} /> Turf area
        </span>
      </Label>

      <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        {editing ? (
          <input
            autoFocus
            inputMode="numeric"
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => {
              const n = Math.max(min, Math.min(max, parseInt(draft || "0", 10)))
              onChange(n)
              setEditing(false)
            }}
            onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur() }}
            aria-label="Turf area in square feet"
            style={{
              fontFamily: FONT_MONO,
              fontSize: 36, fontWeight: 500, color: TURF_INK,
              border: "none", borderBottom: `2px solid ${TURF_ACCENT}`,
              background: "transparent", width: 180, padding: "2px 0",
              letterSpacing: "-0.01em",
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Turf area ${value} square feet, click to edit`}
            style={{
              display: "inline-flex", alignItems: "baseline", gap: 8,
              background: "transparent", border: "none", padding: 0,
              cursor: "text", color: TURF_INK, textAlign: "left",
            }}
          >
            <span style={{ fontFamily: FONT_MONO, fontSize: 36, fontWeight: 500, lineHeight: 1, letterSpacing: "-0.01em" }}>
              {value.toLocaleString()}
            </span>
            <span style={{ fontSize: 13, color: TURF_MUTED }}>sq ft</span>
            <IcoEdit size={14} />
          </button>
        )}
      </div>

      <div style={{ position: "relative", padding: "6px 0 14px" }}>
        <input
          type="range"
          min={0}
          max={1000}
          value={toSlider(value)}
          onChange={(e) => onChange(fromSlider(parseInt(e.target.value, 10)))}
          aria-label="Turf area slider"
          className="turf-range"
          style={{
            width: "100%", height: 6, WebkitAppearance: "none", appearance: "none",
            background: `linear-gradient(to right, ${TURF_ACCENT} 0%, ${TURF_ACCENT} ${toSlider(value) / 10}%, ${TURF_RULE} ${toSlider(value) / 10}%, ${TURF_RULE} 100%)`,
            borderRadius: 3, cursor: "pointer",
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {AREA_PRESETS.map((p) => {
          const active = Math.abs(p.value - value) < 100
          return (
            <button
              type="button"
              key={p.value}
              onClick={() => onChange(p.value)}
              aria-pressed={active}
              style={{
                flex: "1 1 140px",
                padding: "10px 12px",
                background: active ? TURF_INK : "#fff",
                color: active ? "#fff" : TURF_INK,
                border: `1px solid ${active ? TURF_INK : TURF_RULE}`,
                borderRadius: 8,
                boxShadow: active ? `0 0 0 3px ${TURF_ACCENT}26` : "none",
                cursor: "pointer",
                textAlign: "left",
                transition: "all 160ms",
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{p.label}</div>
              <div style={{ fontSize: 11, fontFamily: FONT_MONO, color: active ? "rgba(255,255,255,0.65)" : TURF_MUTED }}>
                {p.value.toLocaleString()} · {p.note}
              </div>
            </button>
          )
        })}
      </div>

      <div style={{ marginTop: 10, fontSize: 12, color: TURF_MUTED }}>
        Not sure how big the area is?{" "}
        <a href="/tools/lawn-size-calculator" style={{ color: TURF_ACCENT, textDecoration: "underline", textUnderlineOffset: 3 }}>
          Trace it on a satellite map
        </a>{" "}
        first.
      </div>
    </div>
  )
}

// ── ZIP field ──────────────────────────────────────────────────────────
const ZIP_FIELD_ID = "turf-form-zip"

function ZipField({
  value,
  onChange,
  region,
  error,
}: {
  value: string
  onChange: (v: string) => void
  region: string
  error?: string
}) {
  const known = value.length === 5 && region !== "Unknown"
  return (
    <div id={ZIP_FIELD_ID} style={{ scrollMarginTop: 100 }}>
      <Label step={2} accent hint={known ? `pricing region: ${region}` : "required, sets your local pricing"}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoPin size={13} /> ZIP code
        </span>
      </Label>
      <ZipCodeInput
        id={ZIP_FIELD_ID + "-input"}
        value={value}
        onChange={onChange}
        error={error}
        showLabel={false}
        enableGeolocation
        rememberLastZip
      />
    </div>
  )
}

// ── Turf grade field ───────────────────────────────────────────────────
function GradeField({ value, onChange }: { value: TurfGrade; onChange: (v: TurfGrade) => void }) {
  return (
    <div>
      <Label step={3} hint={GRADES.find((g) => g.id === value)?.pitch}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoGrade size={13} /> Turf grade
        </span>
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
        {GRADES.map((g) => {
          const active = g.id === value
          return (
            <button
              type="button"
              key={g.id}
              onClick={() => onChange(g.id)}
              aria-pressed={active}
              style={{
                padding: "14px 14px",
                background: active ? TURF_PAPER_DEEP : "#fff",
                color: TURF_INK,
                border: `1px solid ${active ? TURF_INK : TURF_RULE}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                position: "relative",
                transition: "all 160ms",
              }}
            >
              {g.recommended && (
                <span
                  style={{
                    position: "absolute", top: -8, right: 10,
                    background: TURF_ACCENT, color: "#fff",
                    padding: "2px 8px", borderRadius: 999,
                    fontSize: 9.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  }}
                >
                  Most
                </span>
              )}
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{g.name}</div>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 4 }}>
                {g.incl.map((line) => (
                  <li
                    key={line}
                    style={{
                      fontSize: 11.5, color: TURF_MUTED, lineHeight: 1.4,
                      display: "flex", gap: 5, alignItems: "flex-start",
                    }}
                  >
                    <span style={{ color: active ? TURF_ACCENT : TURF_MUTED_2, marginTop: 2 }}>·</span>
                    {line}
                  </li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Install method field ───────────────────────────────────────────────
function MethodField({ value, onChange }: { value: TurfInstallMethod; onChange: (v: TurfInstallMethod) => void }) {
  return (
    <div>
      <Label step={4}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <IcoWrench size={13} /> Install method
        </span>
      </Label>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 8 }}>
        {METHODS.map((m) => {
          const active = m.id === value
          return (
            <button
              type="button"
              key={m.id}
              onClick={() => onChange(m.id)}
              aria-pressed={active}
              style={{
                padding: "14px 14px",
                background: active ? TURF_PAPER_DEEP : "#fff",
                color: TURF_INK,
                border: `1px solid ${active ? TURF_INK : TURF_RULE}`,
                borderRadius: 10,
                cursor: "pointer",
                textAlign: "left",
                transition: "all 160ms",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600 }}>{m.name}</div>
              <div style={{ fontSize: 10.5, fontFamily: FONT_MONO, color: TURF_MUTED, marginTop: 2, letterSpacing: "0.02em" }}>
                {m.meta}
              </div>
              <div style={{ fontSize: 11.5, color: TURF_MUTED, marginTop: 8, lineHeight: 1.45 }}>{m.blurb}</div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Remove existing lawn toggle ────────────────────────────────────────
function RemovalToggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <div>
      <Label step={5} hint="adds demo + haul-off">Prep work</Label>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={on}
        style={{
          width: "100%",
          padding: "12px 14px",
          background: on ? TURF_PAPER_DEEP : "#fff",
          color: TURF_INK,
          border: `1px solid ${on ? TURF_INK : TURF_RULE}`,
          borderRadius: 10,
          cursor: "pointer",
          textAlign: "left",
          display: "flex", alignItems: "center", gap: 12,
          transition: "all 160ms",
        }}
      >
        <span
          style={{
            width: 22, height: 22, borderRadius: 6, flexShrink: 0,
            border: `1.5px solid ${on ? TURF_INK : TURF_RULE}`,
            background: on ? TURF_INK : "#fff",
            color: "#fff",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
          }}
        >
          {on && <IcoCheck size={13} />}
        </span>
        <span style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Remove the existing lawn first</div>
          <div style={{ fontSize: 11, color: TURF_MUTED, marginTop: 1 }}>Strip and haul off the old grass and topsoil before the base goes down</div>
        </span>
      </button>
    </div>
  )
}

// ── ZIP focus helper ───────────────────────────────────────────────────
function focusZipField() {
  if (typeof document === "undefined") return
  const wrapper = document.getElementById(ZIP_FIELD_ID)
  const input = document.getElementById(ZIP_FIELD_ID + "-input") as HTMLInputElement | null
  wrapper?.scrollIntoView({ behavior: "smooth", block: "center" })
  if (input) {
    window.setTimeout(() => input.focus({ preventScroll: true }), 280)
  }
}

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`
const usdRange = (r: { low: number; high: number }) =>
  r.low === r.high ? usd(r.low) : `${usd(r.low)} to ${usd(r.high)}`

// ── Live estimate panel ────────────────────────────────────────────────
function LiveStrip({
  result,
  method,
  needsZip,
}: {
  result: ArtificialTurfCostResult | null
  method: TurfInstallMethod
  needsZip: boolean
}) {
  const rows: Array<{ id: TurfInstallMethod; label: string; high: number; low: number }> = result
    ? [
        { id: "diy", label: "DIY", low: result.diyTotal.low, high: result.diyTotal.high },
        { id: "professional", label: "Pro", low: result.proTotal.low, high: result.proTotal.high },
      ]
    : []
  const max = Math.max(...rows.map((r) => r.high), 1)

  return (
    <div
      style={{
        background: TURF_INK,
        color: "#fff",
        borderRadius: 14,
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.55)", marginBottom: 8,
            fontFamily: FONT_MONO,
          }}
        >
          {needsZip ? "Add your ZIP" : "Estimated total"}
        </div>
        {needsZip ? (
          <button
            type="button"
            onClick={focusZipField}
            style={{
              background: "transparent",
              border: "none",
              padding: 0,
              textAlign: "left",
              cursor: "pointer",
              color: "#fff",
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <span style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.25 }}>
              Enter your ZIP to unlock pricing
            </span>
            <span style={{ fontSize: 12, color: TURF_ACCENT, fontWeight: 600 }}>
              Tap to jump to the ZIP field →
            </span>
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }} aria-live="polite">
            <span
              style={{
                fontFamily: FONT_MONO, fontSize: 28, fontWeight: 500,
                lineHeight: 1.1, letterSpacing: "-0.02em",
              }}
            >
              {result ? usdRange(result.totalCost) : "-"}
            </span>
          </div>
        )}
      </div>

      <div style={{ height: 1, background: "rgba(255,255,255,0.12)" }} />

      <div style={{ display: "grid", gap: 10, fontFamily: FONT_MONO }}>
        <Row k="per sq ft" v={result ? usdRange(result.costPerSqFt) : "-"} />
        <Row k="turf material" v={result ? usdRange(result.materialCost) : "-"} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 4 }}>
        <div
          style={{
            fontSize: 9.5, letterSpacing: "0.14em", textTransform: "uppercase",
            color: "rgba(255,255,255,0.45)", fontFamily: FONT_MONO,
          }}
        >
          DIY vs professional
        </div>
        {rows.map((r) => (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5 }}>
            <span style={{ width: 30, color: r.id === method ? "#fff" : "rgba(255,255,255,0.55)", fontFamily: FONT_MONO }}>
              {r.label}
            </span>
            <div style={{ flex: 1, height: 6, background: "rgba(255,255,255,0.08)", borderRadius: 3, overflow: "hidden" }}>
              <div
                style={{
                  width: `${(r.high / max) * 100}%`,
                  height: "100%",
                  background: r.id === method ? TURF_ACCENT : "rgba(255,255,255,0.35)",
                  transition: "width 220ms cubic-bezier(.2,.7,.3,1)",
                }}
              />
            </div>
            <span
              style={{
                fontFamily: FONT_MONO,
                color: r.id === method ? "#fff" : "rgba(255,255,255,0.55)",
                width: 96, textAlign: "right",
              }}
            >
              {usdRange({ low: r.low, high: r.high })}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: 12 }}>
      <span style={{ color: "rgba(255,255,255,0.45)" }}>{k}</span>
      <span style={{ color: "#fff" }}>{v}</span>
    </div>
  )
}

// ── Viewport hook ──────────────────────────────────────────────────────
function useIsNarrow(breakpoint = 768) {
  const [narrow, setNarrow] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
    const onChange = () => setNarrow(mq.matches)
    onChange()
    mq.addEventListener("change", onChange)
    return () => mq.removeEventListener("change", onChange)
  }, [breakpoint])
  return narrow
}

// ── The whole calculator ───────────────────────────────────────────────
export function ArtificialTurfCostForm({ initialValues, onResultChange, error }: ArtificialTurfCostFormProps) {
  const searchParams = usePrerenderSafeSearchParams()

  // Seed initial state from URL params if present, otherwise sensible defaults.
  const initial = useMemo(() => {
    const sp = searchParams
    const sizeRaw = sp?.get("size")
    const sizeNum = sizeRaw ? parseInt(sizeRaw, 10) : NaN
    const zipRaw = sp?.get("zip") ?? initialValues?.zipCode ?? ""
    const gradeRaw = sp?.get("grade")?.toLowerCase()
    const installRaw = sp?.get("install")?.toLowerCase()
    const removeRaw = sp?.get("remove")
    const validGrade: TurfGrade | null =
      gradeRaw === "economy" || gradeRaw === "standard" || gradeRaw === "premium" ? (gradeRaw as TurfGrade) : null
    const validInstall: TurfInstallMethod | null =
      installRaw === "diy" || installRaw === "professional" ? (installRaw as TurfInstallMethod) : null
    const cleanedZip = zipRaw.replace(/[^0-9]/g, "").slice(0, 5)
    return {
      area: !isNaN(sizeNum) && sizeNum >= 100 ? sizeNum : initialValues?.area ?? 800,
      zip: cleanedZip,
      grade: validGrade ?? ("standard" as TurfGrade),
      method: validInstall ?? ("professional" as TurfInstallMethod),
      removeExistingLawn: removeRaw === "1" || removeRaw === "true",
    }
    // Lock to first render - URL params seed initial state only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [area, setArea] = useState(initial.area)
  const [zip, setZip] = useState(initial.zip)
  const [grade, setGrade] = useState<TurfGrade>(initial.grade)
  const [method, setMethod] = useState<TurfInstallMethod>(initial.method)
  const [removeExistingLawn, setRemoveExistingLawn] = useState(initial.removeExistingLawn)
  const [zipError, setZipError] = useState<string | undefined>(undefined)
  const [zipTouched, setZipTouched] = useState(false)

  const region = useMemo(() => getRegionFromZipCode(zip), [zip])
  const zipKnown = zip.length === 5 && region !== "Unknown"

  const { savedInputs, saveInputs } = useSavedInputs()
  const editedRef = useRef({ zip: false, size: false })

  // Seed ZIP from a saved profile / cross-tool value if the user hasn't typed one.
  useEffect(() => {
    if (initialValues?.zipCode && /^\d{5}$/.test(initialValues.zipCode) && !editedRef.current.zip && !zip) {
      setZip(initialValues.zipCode)
    }
    if (initialValues?.area && !editedRef.current.size && area === 800) {
      setArea(initialValues.area)
    }
  }, [initialValues, zip, area])

  const seededZipFromSaved = useRef(initial.zip.length === 5)
  useEffect(() => {
    if (seededZipFromSaved.current) return
    if (savedInputs.zip && /^\d{5}$/.test(savedInputs.zip)) {
      setZip(savedInputs.zip)
      seededZipFromSaved.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedInputs.zip])

  // Persist area to localStorage so other tools prefill. Skip the first render
  // so the form's initial state doesn't overwrite a richer saved value.
  const sizeWriteReady = useRef(false)
  useEffect(() => {
    if (!sizeWriteReady.current) {
      sizeWriteReady.current = true
      return
    }
    if (area > 0) saveInputs({ lawnSize: area })
  }, [area, saveInputs])

  // Live estimate - only when we have a usable ZIP.
  const liveResult = useMemo<ArtificialTurfCostResult | null>(() => {
    if (!zipKnown) return null
    try {
      return calculateArtificialTurfCost(area, grade, method, zip, removeExistingLawn)
    } catch {
      return null
    }
  }, [zipKnown, area, grade, method, zip, removeExistingLawn])

  // Stream the live result up to the page so it renders the result card +
  // breakdown without a submit step. Null until the ZIP resolves.
  useEffect(() => {
    onResultChange?.(liveResult, { removeExistingLawn })
  }, [liveResult, removeExistingLawn, onResultChange])

  const isNarrow = useIsNarrow()
  const mobileDockRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isNarrow) {
      setBottomDockHeight(0)
      return
    }

    const dock = mobileDockRef.current
    if (!dock) return

    const publishDockHeight = () => {
      setBottomDockHeight(dock.getBoundingClientRect().height)
    }

    publishDockHeight()
    const observer = new ResizeObserver(publishDockHeight)
    observer.observe(dock)
    window.addEventListener("resize", publishDockHeight)

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", publishDockHeight)
      setBottomDockHeight(0)
    }
  }, [isNarrow])

  const handleZipChange = useCallback((v: string) => {
    editedRef.current.zip = true
    setZip(v)
    setZipTouched(true)
    if (v.length === 5 && getRegionFromZipCode(v) === "Unknown") {
      setZipError("That ZIP isn't recognized. Try a five-digit US ZIP.")
    } else {
      setZipError(undefined)
    }
  }, [])

  return (
    <form
      // Kept as a <form> (no submit UI) so the postal-code input keeps its
      // accessible grouping + browser autofill context. The result is live - it
      // streams up via onResultChange.
      onSubmit={(e) => e.preventDefault()}
      style={{
        background: TURF_BG,
        color: TURF_INK,
        fontFamily: "var(--font-sans, 'Inter', system-ui, sans-serif)",
        borderRadius: 18,
        border: `1px solid ${TURF_RULE}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {/* Slider thumb styling - scoped via class */}
      <style>{`
        .turf-range:focus-visible {
          outline: 3px solid color-mix(in srgb, ${TURF_ACCENT} 45%, transparent);
          outline-offset: 4px;
        }
        .turf-range::-webkit-slider-thumb {
          -webkit-appearance: none; appearance: none;
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${TURF_ACCENT};
          box-shadow: 0 1px 4px rgba(0,0,0,0.15);
          cursor: grab;
        }
        .turf-range::-moz-range-thumb {
          width: 22px; height: 22px; border-radius: 50%;
          background: #fff; border: 2px solid ${TURF_ACCENT};
          cursor: grab;
        }
      `}</style>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isNarrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) 280px",
          gap: isNarrow ? 24 : 32,
          padding: isNarrow
            ? "24px 20px calc(var(--wg-bottom-dock-height, 120px) + 24px)"
            : "32px 32px 36px",
          scrollPaddingBottom: isNarrow
            ? "calc(var(--wg-bottom-dock-height, 120px) + 16px)"
            : undefined,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 14,
            background: TURF_PAPER_DEEP,
            borderRadius: 14,
            padding: 12,
          }}
        >
          <SectionCard>
            <AreaField
              value={area}
              onChange={(next) => {
                editedRef.current.size = true
                setArea(next)
              }}
            />
          </SectionCard>
          <SectionCard>
            <ZipField value={zip} onChange={handleZipChange} region={region} error={zipTouched ? zipError : undefined} />
          </SectionCard>
          <SectionCard>
            <GradeField value={grade} onChange={setGrade} />
          </SectionCard>
          <SectionCard>
            <MethodField value={method} onChange={setMethod} />
          </SectionCard>
          <SectionCard>
            <RemovalToggle on={removeExistingLawn} onClick={() => setRemoveExistingLawn(!removeExistingLawn)} />
          </SectionCard>

          {error && (
            <div role="alert" style={{ padding: 12, background: "#fdf2f0", border: "1px solid #e9c4be", borderRadius: 8, fontSize: 13, color: "#7a2418" }}>
              {error}
            </div>
          )}
        </div>

        {/* Right rail (desktop) */}
        {!isNarrow && (
          <aside style={{ alignSelf: "flex-start", position: "sticky", top: 24, display: "flex", flexDirection: "column", gap: 14 }}>
            <LiveStrip result={liveResult} method={method} needsZip={!zipKnown} />
            <div style={{ fontSize: 11.5, color: TURF_MUTED, lineHeight: 1.5, display: "flex", gap: 8 }}>
              <IcoLock size={12} />
              <span>
                These are estimate ranges, not quotes. Installed turf pricing swings widely, so get local quotes before you budget.
              </span>
            </div>
          </aside>
        )}
      </div>

      {/* Mobile docked estimate - fixed to viewport so the form's overflow:hidden doesn't break stickiness */}
      {isNarrow && (
        <div
          ref={mobileDockRef}
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 30,
            boxShadow: "0 -8px 30px rgba(0,0,0,0.10)",
            background: TURF_INK,
            paddingBottom: "env(safe-area-inset-bottom)",
          }}
        >
          <div style={{ padding: "14px 16px 12px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, color: "#fff" }}>
              {liveResult ? (
                <div>
                  <div
                    style={{
                      fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase",
                      color: "rgba(255,255,255,0.55)", fontFamily: FONT_MONO, marginBottom: 4,
                    }}
                  >
                    Estimated total
                  </div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }} aria-live="polite">
                    <span style={{ fontFamily: FONT_MONO, fontSize: 22, fontWeight: 500, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
                      {usdRange(liveResult.totalCost)}
                    </span>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={focusZipField}
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    textAlign: "left",
                    cursor: "pointer",
                    color: "#fff",
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  <span
                    style={{
                      fontSize: 9.5, letterSpacing: "0.16em", textTransform: "uppercase",
                      color: "rgba(255,255,255,0.55)", fontFamily: FONT_MONO,
                    }}
                  >
                    To see your estimate
                  </span>
                  <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.2 }}>
                    Add your ZIP code <span style={{ color: TURF_ACCENT }}>→</span>
                  </span>
                </button>
              )}
              {liveResult ? (
                <a
                  href="#results"
                  style={{
                    background: TURF_ACCENT,
                    color: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "10px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    whiteSpace: "nowrap",
                    textDecoration: "none",
                  }}
                >
                  Breakdown
                  <IcoChev size={14} />
                </a>
              ) : (
                <button
                  type="button"
                  onClick={focusZipField}
                  style={{
                    background: TURF_ACCENT,
                    color: "#fff",
                    border: "none",
                    borderRadius: 999,
                    padding: "10px 16px",
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  Add ZIP
                  <IcoChev size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
